import React, { useEffect, useMemo, useRef, useState } from 'react';

import { debounce } from 'lodash';
import { useSnackbar } from 'notistack';

import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TablePagination,
  TextField,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import AccommodationPropertyStateIndicator from '~/components/Accommodation/Pages/Properties/components/AccommodationPropertyStateIndicator';

import { useGetAccommodationPropertiesByIds } from '~/hooks/apis/accommodation/useGetAccommodationProperties';
import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import { formatDateSlashesWithClock } from '~/services/TimeService';

import { useHotelOffersByFilters, useHotelOffersByPlaceIds } from '../../hooks';
import type { ExperienceOfferItem } from '../../hooks';
import { useDeleteAllTagAssignments, useDeleteTagAssignment } from '../../hooks/useTagAssignments';
import { useTagAssignmentsForForm } from '../../hooks/useTagAssignmentsForForm';
import {
  DATAGRID_BORDERED_CONTAINER_SX,
  DATAGRID_COMPACT_EMPTY_OVERLAY_SX,
  DATAGRID_EXPAND_TO_ROWS_SX,
  DATAGRID_HIDE_SEPARATORS_SX,
  DELETE_ALL_HEADER_BUTTON_SX,
} from '../../styles';
import TagAssignmentsDestinationSearch from '../TagAssignmentsDestination/TagAssignmentsDestinationSearch';
import TagAssignmentsOfferList from '../TagAssignmentsOfferList';

import HotelIdEntry from './HotelIdEntry';
import PassportHotelSelection from './PassportHotelSelection';
import { getNextTaggedHotelPage } from './getNextTaggedHotelPage';

type HotelSelectionMode = 'search' | 'direct-id';

enum SearchModeOptions {
  ID = 'idIn',
  TITLE = 'title',
  LOCATION = 'location',
}

const searchModeOptionsLabels = {
  [SearchModeOptions.ID]: 'ID',
  [SearchModeOptions.TITLE]: 'Title',
  [SearchModeOptions.LOCATION]: 'Location',
};

interface Props {
  tagId?: string;
  entityIdFilter: string;
  selectedOfferIds: Array<string>;
  onSelectionChange: (selectedIds: Array<string>) => void;
  savedOfferIds?: Array<string>;
  isPassportTag?: boolean;
  selectionMode?: HotelSelectionMode;
  isSaving?: boolean;
  disabled?: boolean;
  experienceOfferId?: string;
  experienceOffer?: ExperienceOfferItem;
}

export default function TagAssignmentsHotel({
  tagId,
  entityIdFilter,
  selectedOfferIds,
  onSelectionChange,
  savedOfferIds = [],
  isPassportTag = false,
  selectionMode = 'search',
  isSaving = false,
  disabled = false,
  experienceOfferId,
  experienceOffer,
}: Props) {
  const isDirectIdMode = selectionMode === 'direct-id';
  const [placeIds, setPlaceIds] = useState<Array<string>>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<string | null>(null);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [searchModeSelected, setSearchModeSelected] = useState<SearchModeOptions>(SearchModeOptions.LOCATION);
  const [fieldSearchValue, setFieldSearchValue] = useState('');
  const [debouncedFieldSearchValue, setDebouncedFieldSearchValue] = useState('');
  const [pendingSelectedOfferIds, setPendingSelectedOfferIds] = useState<Array<string> | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const { paginationModel: savedPaginationModel, setPaginationModel: setSavedPaginationModel } = useDataGridPagination({
    pageSize: 3,
    page: 0,
  });

  const { savedAssignments, isLoading: isLoadingAssignments } = useTagAssignmentsForForm({
    tagId,
  });

  const deleteTagAssignment = useDeleteTagAssignment();
  const deleteAllTagAssignments = useDeleteAllTagAssignments();

  const hotelAssignments = savedAssignments.filter((a) => a.entityType === 'HOTEL');
  const savedHotelOfferIds = hotelAssignments.map((assignment) => assignment.entityId);
  const draftHotelOfferIds = selectedOfferIds.filter((offerId) => !savedHotelOfferIds.includes(offerId));
  const effectiveSelectedOfferIds = pendingSelectedOfferIds || selectedOfferIds;
  const hotelRows = useMemo(() => {
    const savedRows = hotelAssignments.map((assignment) => ({
      ...assignment,
      rowId: assignment.id,
      isDraft: false,
    }));

    const draftRows = draftHotelOfferIds.map((offerId) => ({
      id: offerId,
      rowId: `draft-${offerId}`,
      entityId: offerId,
      isDraft: true,
    }));

    return [...savedRows, ...draftRows];
  }, [draftHotelOfferIds, hotelAssignments]);
  const normalizedEntityIdFilter = entityIdFilter.trim().toLowerCase();
  const filteredHotelRows = useMemo(() => {
    if (!normalizedEntityIdFilter) {
      return hotelRows;
    }

    return hotelRows.filter((row) => row.entityId.toLowerCase().includes(normalizedEntityIdFilter));
  }, [hotelRows, normalizedEntityIdFilter]);
  const hasEntityIdFilter = normalizedEntityIdFilter.length > 0;
  const visibleHotelAssignments = useMemo(() => {
    const start = savedPaginationModel.page * savedPaginationModel.pageSize;
    const end = start + savedPaginationModel.pageSize;

    return filteredHotelRows.slice(start, end);
  }, [filteredHotelRows, savedPaginationModel.page, savedPaginationModel.pageSize]);
  const visibleHotelEntityIds = useMemo(
    () => Array.from(new Set(visibleHotelAssignments.map((row) => row.entityId))),
    [visibleHotelAssignments],
  );
  const { offers: taggedHotelOffers, isLoading: isLoadingTaggedHotelOffers } = useHotelOffersByFilters({
    idIn: visibleHotelEntityIds.join(',') || null,
    enabled: visibleHotelEntityIds.length > 0 && !isLoadingAssignments,
  });
  const hotelOfferNamesMap = useMemo(() => {
    const map = new Map<string, string>();
    taggedHotelOffers.forEach((offer) => {
      map.set(offer.id, offer.title);
    });
    return map;
  }, [taggedHotelOffers]);

  const taggedPropertyIds = useMemo(
    () => new Map(taggedHotelOffers.map((offer) => [offer.id, offer.propertyId] as const)),
    [taggedHotelOffers],
  );
  const taggedPropertyIdList = Array.from(
    new Set(taggedHotelOffers.map((offer) => offer.propertyId).filter((id): id is string => !!id)),
  );
  const { propertiesById: taggedPropertiesById, isLoading: isLoadingTaggedProperties } =
    useGetAccommodationPropertiesByIds(isPassportTag ? taggedPropertyIdList : []);
  const isLoadingTaggedHotelProperties = isLoadingTaggedHotelOffers || isLoadingTaggedProperties;
  const previousHotelRowCountRef = useRef(hotelRows.length);
  const previousEntityIdFilterRef = useRef(normalizedEntityIdFilter);
  const pendingHotelAddTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleDeleteClick = (assignmentId: string) => {
    if (savedAssignments.length === 1) {
      enqueueSnackbar('Cannot delete the unique assignment. A tag must have at least one assignment.', {
        variant: 'error',
      });
      return;
    }
    setAssignmentToDelete(assignmentId);
    setDeleteDialogOpen(true);
  };

  const savedAssignmentsColumns: Array<GridColDef> = useMemo(
    () => [
      {
        field: 'entityId',
        headerName: 'Entity ID',
        flex: 1,
        minWidth: 150,
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          if (isLoadingTaggedHotelOffers && !hotelOfferNamesMap.has(params.row.entityId)) {
            return (
              <Box display="flex" alignItems="center" height="100%">
                <CircularProgress size={16} />
              </Box>
            );
          }
          const offerName = hotelOfferNamesMap.get(params.row.entityId) || params.row.entityId;
          return (
            <Box display="flex" alignItems="center" height="100%">
              {offerName}
            </Box>
          );
        },
      },
      ...(isPassportTag
        ? [
            {
              field: 'hotelType',
              headerName: 'Hotel type',
              flex: 1,
              minWidth: 260,
              renderCell: (params) => {
                const propertyId = taggedPropertyIds.get(params.row.entityId);
                const property = propertyId && taggedPropertiesById.get(propertyId);

                if (property) {
                  return <AccommodationPropertyStateIndicator property={property} />;
                }
                return isLoadingTaggedHotelProperties ? <CircularProgress size={16} /> : '—';
              },
            } satisfies GridColDef,
          ]
        : []),
      {
        field: 'createdAt',
        headerName: 'Assignment time',
        flex: 1,
        minWidth: 180,
        renderCell: (params) => (
          <Box display="flex" alignItems="center" height="100%">
            {params.row.isDraft ? '—' : params.row.createdAt ? formatDateSlashesWithClock(params.row.createdAt) : '—'}
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 160,
        sortable: false,
        disableColumnMenu: true,
        align: 'right',
        headerAlign: 'right',
        renderHeader: () => {
          if (filteredHotelRows.length === 0) {
            return null;
          }

          return (
            <Button
              variant="text"
              color="error"
              size="small"
              onClick={handleDeleteAllClick}
              disabled={disabled || deleteAllTagAssignments.isPending}
              sx={DELETE_ALL_HEADER_BUTTON_SX}
              endIcon={
                deleteAllTagAssignments.isPending ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />
              }
            >
              Delete all
            </Button>
          );
        },
        renderCell: (params) => {
          if (params.row.isDraft) {
            return (
              <IconButton
                size="small"
                color="error"
                onClick={() => onSelectionChange(selectedOfferIds.filter((id) => id !== params.row.entityId))}
                disabled={disabled}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            );
          }

          const isDeleting = deleteTagAssignment.isPending && assignmentToDelete === params.row.id;
          return (
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(params.row.id)}
              disabled={disabled || isDeleting}
            >
              {isDeleting ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
            </IconButton>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      deleteAllTagAssignments.isPending,
      deleteTagAssignment.isPending,
      assignmentToDelete,
      disabled,
      filteredHotelRows.length,
      hotelOfferNamesMap,
      isPassportTag,
      isLoadingTaggedHotelProperties,
      isLoadingTaggedHotelOffers,
      onSelectionChange,
      selectedOfferIds,
      taggedPropertyIds,
      taggedPropertiesById,
    ],
  );

  const handleDeleteConfirm = async () => {
    if (!assignmentToDelete) {
      return;
    }
    await deleteTagAssignment.mutateAsync(assignmentToDelete);
    setDeleteDialogOpen(false);
    setAssignmentToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAssignmentToDelete(null);
  };

  const handleDeleteAllClick = () => {
    setDeleteAllDialogOpen(true);
  };

  const handleDeleteAllConfirm = async () => {
    const assignmentIds = hotelAssignments.map((a) => a.id);

    if (assignmentIds.length > 0) {
      await deleteAllTagAssignments.mutateAsync(assignmentIds);
    }

    onSelectionChange([]);
    setDeleteAllDialogOpen(false);
  };

  const handleDeleteAllCancel = () => {
    setDeleteAllDialogOpen(false);
  };

  const handleDestinationsChange = (newPlaceIds: Array<string>) => {
    setPlaceIds(newPlaceIds);
  };

  const handleDestinationRemove = (placeIdToRemove: string) => {
    const offerIdsToRemove = offerIdsByPlaceId[placeIdToRemove] || [];
    const savedOfferIdsSet = new Set(savedOfferIds);

    if (offerIdsToRemove.length > 0) {
      const offerIdsToRemoveSet = new Set(offerIdsToRemove);
      const updatedSelectedIds = selectedOfferIds.filter(
        (id) => !offerIdsToRemoveSet.has(id) || savedOfferIdsSet.has(id),
      );
      onSelectionChange(updatedSelectedIds);
    }

    setPlaceIds((prev) => prev.filter((id) => id !== placeIdToRemove));
  };

  const handleSearchModeChange = (event: SelectChangeEvent<SearchModeOptions>) => {
    setSearchModeSelected(event.target.value as SearchModeOptions);
    setFieldSearchValue('');
    setDebouncedFieldSearchValue('');
    setPlaceIds([]);
  };

  const handleDelayedSelectionChange = (nextSelectedIds: Array<string>) => {
    const currentSelectedIds = pendingSelectedOfferIds || selectedOfferIds;

    if (nextSelectedIds.length > currentSelectedIds.length) {
      const nextDraftCount = nextSelectedIds.filter((offerId) => !savedHotelOfferIds.includes(offerId)).length;
      const nextRowCount = hotelAssignments.length + nextDraftCount;
      const lastPage = Math.max(0, Math.ceil(nextRowCount / savedPaginationModel.pageSize) - 1);

      setPendingSelectedOfferIds(nextSelectedIds);
      setSavedPaginationModel((currentModel) => ({
        ...currentModel,
        page: lastPage,
      }));

      if (pendingHotelAddTimeoutRef.current) {
        clearTimeout(pendingHotelAddTimeoutRef.current);
      }

      pendingHotelAddTimeoutRef.current = setTimeout(() => {
        onSelectionChange(nextSelectedIds);
        setPendingSelectedOfferIds(null);
        pendingHotelAddTimeoutRef.current = null;
      }, 500);

      return;
    }

    if (pendingHotelAddTimeoutRef.current) {
      clearTimeout(pendingHotelAddTimeoutRef.current);
      pendingHotelAddTimeoutRef.current = null;
    }

    setPendingSelectedOfferIds(null);
    onSelectionChange(nextSelectedIds);
  };

  const handleSavedPageChange = (_: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    setSavedPaginationModel((currentModel) => ({
      ...currentModel,
      page,
    }));
  };

  const handleSavedRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSavedPaginationModel((currentModel) => ({
      ...currentModel,
      page: 0,
      pageSize: Number(event.target.value),
    }));
  };

  const debouncedSetSearchValue = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedFieldSearchValue(value);
      }, 500),
    [],
  );

  useEffect(() => {
    debouncedSetSearchValue(fieldSearchValue);
    return () => {
      debouncedSetSearchValue.cancel();
    };
  }, [fieldSearchValue, debouncedSetSearchValue]);

  useEffect(() => {
    return () => {
      if (pendingHotelAddTimeoutRef.current) {
        clearTimeout(pendingHotelAddTimeoutRef.current);
      }
    };
  }, []);

  const isLocationMode = searchModeSelected === SearchModeOptions.LOCATION;
  const hasSearchValue = debouncedFieldSearchValue.trim().length > 0;
  const shouldSearchByFilters =
    !isDirectIdMode &&
    !isLocationMode &&
    hasSearchValue &&
    (searchModeSelected === SearchModeOptions.ID || debouncedFieldSearchValue.length >= 2);

  const filters = useMemo(() => {
    if (!shouldSearchByFilters) {
      return {};
    }
    const filterObj: { idIn?: string | null; title?: string | null } = {};
    if (searchModeSelected === SearchModeOptions.ID) {
      filterObj.idIn = debouncedFieldSearchValue.trim();
    } else if (searchModeSelected === SearchModeOptions.TITLE) {
      filterObj.title = debouncedFieldSearchValue.trim();
    }
    return filterObj;
  }, [shouldSearchByFilters, searchModeSelected, debouncedFieldSearchValue]);

  const {
    offers: hotelOffersByPlaceIds,
    isLoading: isLoadingHotelsByPlaceIds,
    offerIdsByPlaceId,
  } = useHotelOffersByPlaceIds({
    placeIds,
    enabled: isDirectIdMode ? false : isLocationMode && placeIds.length > 0,
  });

  const { offers: hotelOffersByFilters, isLoading: isLoadingHotelsByFilters } = useHotelOffersByFilters({
    idIn: filters.idIn,
    title: filters.title,
    enabled: shouldSearchByFilters,
  });

  const hotelOffers = isLocationMode ? hotelOffersByPlaceIds : hotelOffersByFilters;
  const isLoadingHotels = isLocationMode ? isLoadingHotelsByPlaceIds : isLoadingHotelsByFilters;

  const shouldShowResults = isLocationMode ? placeIds.length > 0 : shouldSearchByFilters;

  useEffect(() => {
    if (!isDirectIdMode) {
      return;
    }

    if (pendingHotelAddTimeoutRef.current) {
      clearTimeout(pendingHotelAddTimeoutRef.current);
      pendingHotelAddTimeoutRef.current = null;
    }

    setPendingSelectedOfferIds(null);
    setPlaceIds([]);
    setFieldSearchValue('');
    setDebouncedFieldSearchValue('');
  }, [isDirectIdMode]);

  useEffect(() => {
    if (searchModeSelected !== SearchModeOptions.LOCATION) {
      setPlaceIds([]);
    }
    setFieldSearchValue('');
    setDebouncedFieldSearchValue('');
  }, [searchModeSelected]);

  useEffect(() => {
    const previousRowCount = previousHotelRowCountRef.current;
    const nextRowCount = hotelRows.length;
    const filterChanged = previousEntityIdFilterRef.current !== normalizedEntityIdFilter;
    const nextPage = getNextTaggedHotelPage({
      currentPage: savedPaginationModel.page,
      filteredRowCount: filteredHotelRows.length,
      filterChanged,
      hasFilter: hasEntityIdFilter,
      pageSize: savedPaginationModel.pageSize,
      rowsAdded: nextRowCount > previousRowCount,
      totalRowCount: nextRowCount,
    });

    previousHotelRowCountRef.current = nextRowCount;
    previousEntityIdFilterRef.current = normalizedEntityIdFilter;

    if (nextPage !== savedPaginationModel.page) {
      setSavedPaginationModel((currentModel) => ({
        ...currentModel,
        page: nextPage,
      }));
    }
  }, [
    filteredHotelRows.length,
    hasEntityIdFilter,
    hotelRows.length,
    normalizedEntityIdFilter,
    savedPaginationModel.page,
    savedPaginationModel.pageSize,
    setSavedPaginationModel,
  ]);

  return (
    <Box>
      {tagId && isLoadingAssignments ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          <Box sx={DATAGRID_BORDERED_CONTAINER_SX}>
            <DataGrid
              rows={visibleHotelAssignments}
              columns={savedAssignmentsColumns}
              getRowId={(row) => row.rowId}
              localeText={{ noRowsLabel: entityIdFilter.trim() ? 'No tagged hotels match this entity ID' : 'None' }}
              disableRowSelectionOnClick
              disableColumnMenu
              hideFooter
              sx={[
                { border: 0 },
                DATAGRID_EXPAND_TO_ROWS_SX,
                DATAGRID_COMPACT_EMPTY_OVERLAY_SX,
                DATAGRID_HIDE_SEPARATORS_SX,
              ]}
            />
            {filteredHotelRows.length > 0 && (
              <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
                <TablePagination
                  component="div"
                  count={filteredHotelRows.length}
                  page={savedPaginationModel.page}
                  onPageChange={handleSavedPageChange}
                  rowsPerPage={savedPaginationModel.pageSize}
                  onRowsPerPageChange={handleSavedRowsPerPageChange}
                  rowsPerPageOptions={[3, 10, 25, 50]}
                />
              </Box>
            )}
          </Box>
          {isDirectIdMode ? (
            <HotelIdEntry
              selectedIds={selectedOfferIds}
              disabled={disabled || isSaving}
              onAdd={(offerId) => onSelectionChange([...selectedOfferIds, offerId])}
            />
          ) : isPassportTag ? (
            <PassportHotelSelection
              experienceOfferId={experienceOfferId}
              experienceOffer={experienceOffer}
              selectedOfferIds={effectiveSelectedOfferIds}
              onSelectionChange={handleDelayedSelectionChange}
              savedOfferIds={savedOfferIds}
            />
          ) : (
            <>
              <Box>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box minWidth={150}>
                    <Select value={searchModeSelected} onChange={handleSearchModeChange} size="small" fullWidth>
                      {Object.entries(searchModeOptionsLabels).map(([key, label]) => (
                        <MenuItem key={key} value={key}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                  <Box flexGrow={1}>
                    {isLocationMode ? (
                      <TagAssignmentsDestinationSearch
                        onSelectDestinations={handleDestinationsChange}
                        onRemoveDestination={handleDestinationRemove}
                        savedPlaceIds={[]}
                        selectedPlaceIds={placeIds}
                      />
                    ) : (
                      <TextField
                        fullWidth
                        size="small"
                        value={fieldSearchValue}
                        onChange={(e) => setFieldSearchValue(e.target.value)}
                        autoComplete="off"
                        placeholder="Search for hotels to tag"
                      />
                    )}
                  </Box>
                </Stack>
              </Box>
              {shouldShowResults && (
                <TagAssignmentsOfferList
                  offers={hotelOffers}
                  selectedOfferIds={effectiveSelectedOfferIds}
                  onSelectionChange={handleDelayedSelectionChange}
                  isLoading={isLoadingHotels}
                  savedOfferIds={savedOfferIds}
                  offerLabel="Hotels"
                  showHotelType
                  emptyMessage="No hotel offers found matching your search criteria."
                />
              )}
            </>
          )}
        </Stack>
      )}
      <Dialog open={deleteDialogOpen} onClose={deleteTagAssignment.isPending ? undefined : handleDeleteCancel}>
        <DialogTitle>Delete Assignment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this assignment? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} variant="text" disabled={deleteTagAssignment.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            autoFocus
            disabled={deleteTagAssignment.isPending}
            startIcon={deleteTagAssignment.isPending ? <CircularProgress size={16} /> : null}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteAllDialogOpen}
        onClose={deleteAllTagAssignments.isPending ? undefined : handleDeleteAllCancel}
      >
        <DialogTitle>Delete All Hotel Assignments</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete all {hotelAssignments.length} hotel assignment(s)? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteAllCancel} variant="text" disabled={deleteAllTagAssignments.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAllConfirm}
            variant="contained"
            color="error"
            autoFocus
            disabled={deleteAllTagAssignments.isPending}
            startIcon={deleteAllTagAssignments.isPending ? <CircularProgress size={16} /> : null}
          >
            Delete All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
